import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  TableHead,
  Button,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// dnd-kit
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import LaneSortableItem from './LaneSortableItem';
import ActionColumn, { ActionColumnHeader } from './ActionColumn';
import ResultModal from './ResultModal';
import { useScoreboardUpdater } from '../../hooks/useScoreboardUpdater'

import { useEventContext } from '../../context/EventContext';
import { calculateBestMark, assignPlaces } from '../../utils/ResultUtils';
import DownloadHorizontalLffButton from './DownloadHorizontalLffButton';
import { computeGlobalPlaces } from '../../utils/globalRankings';

// Constantes para identificar tipos de eventos
const JUMP_EVENTS = ['LJ1', 'TJ1']; // Eventos de salto (pueden tener viento)
const THROW_EVENTS = ['DT1', 'HT1', 'SP1', 'JT1']; // Eventos de lanzamiento
const ALL_HORIZONTAL_EVENTS = [...JUMP_EVENTS, ...THROW_EVENTS];

const HorizontalEvents = ({ heatDetails }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600px
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // <900px
  
  // Configuración de estilos responsivos basados en tamaño de pantalla
  const responsiveStyles = useMemo(() => ({
    tableCell: {
      padding: isMobile ? '4px 2px' : isTablet ? '6px 4px' : '16px 8px',
      fontSize: isMobile ? '0.7rem' : isTablet ? '0.8rem' : '0.875rem',
    },
    button: {
      minWidth: isMobile ? '40px' : isTablet ? '60px' : '80px',
      padding: isMobile ? '2px 4px' : isTablet ? '4px 8px' : '8px 16px',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.75rem' : '0.875rem',
    },
    inputField: {
      width: isMobile ? '60px' : isTablet ? '80px' : '120px',
    },
    typography: {
      nameSize: isMobile ? '0.7rem' : isTablet ? '0.8rem' : '0.875rem',
      teamSize: isMobile ? '0.6rem' : isTablet ? '0.65rem' : '0.75rem'
    }
  }), [isMobile, isTablet]);
  
  const {
    updateParticipantResults,
    updateHeatParticipants,
    getParticipantResults,
    calculateRankings,
    isTrackEventFn,
    rankings,
    testSettings,
    calculateBestMark,
    pplData,
    saveSeedMarks,
    getSeedMarks,
    getPlace,
    hasPassed,
    participantsResults,
    boardConfigs,
    refreshTablero, // Para actualizar tableros
  } = useEventContext();

  // ✅ NUEVO: Estado para el atleta activo
  const [activeAthlete, setActiveAthlete] = useState(null);
  const [activeAttempt, setActiveAttempt] = useState(null);

  // Para mostrar alertas
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Modal de resultados
  const [openResultModal, setOpenResultModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [initialValues, setInitialValues] = useState({});

  // Modal de marca de siembra
  const [openSeedMarkModal, setOpenSeedMarkModal] = useState(false);
  const [selectedSeedParticipant, setSelectedSeedParticipant] = useState(null);
  const [seedMarks, setSeedMarks] = useState({});

  // Array de objetos (participantes) con lugares
  const [allParticipantsWithPlaces, setAllParticipantsWithPlaces] = useState([]);

  // Aquí guardaremos la configuración de cuántos intentos hay
  const [attempts, setAttempts] = useState([]);

  // Estados especiales
  const specialStatuses = ['DNS', 'DNF', 'DQ', 'SCR', 'NM'];

  // Participantes plano
  const participantsArray = Array.isArray(heatDetails.participants)
    ? heatDetails.participants
    : [];

  // Determinar tipo de evento
  const isJumpEvent = JUMP_EVENTS.includes(heatDetails.eventCode);
  const isThrowEvent = THROW_EVENTS.includes(heatDetails.eventCode);
  const isHorizontalEvent = ALL_HORIZONTAL_EVENTS.includes(heatDetails.eventCode);

  // Configuración del evento
  const heatKey = `${heatDetails.eventNumber}_${heatDetails.round}_${heatDetails.heat}`;
  const eventSettings = testSettings[heatKey] || {};
  const enablePoints = eventSettings.enablePoints || false;
  const eventCode = heatDetails.eventCode;
  const { selectAthlete, saveResult, clear } = useScoreboardUpdater()

  const updateTablerosWithActiveAthlete = useCallback((athleteData, attemptNumber) => {
    console.log('[updateTableros] === INICIO ===');
    console.log('[updateTableros] Datos recibidos:', {
      athleteData,
      attemptNumber,
      athleteName: athleteData?.name || athleteData?.fullName,
      hasCurrentResult: athleteData?.currentResult
    });
  
    // 1) Buscar tableros asignados a este heat
    const assignedBoards = boardConfigs.filter(cfg =>
      cfg.settings?.assignedHeat === heatKey
    );
    console.log('[updateTableros] Tableros encontrados:', assignedBoards.length);
    if (assignedBoards.length === 0) {
      console.log('[updateTableros] No hay tableros asignados');
      return;
    }
  
    // 2) Para cada tablero, construimos y enviamos el eventData adecuado
    assignedBoards.forEach(cfg => {
      console.log(`[updateTableros] Procesando tablero: ${cfg.name}`);
      const rows = cfg.settings?.displayRows || 1;
      console.log(`[updateTableros] DisplayRows: ${rows}`);
  
      let participantsForBoard, eventDataForBoard;
  
      if (rows === 1) {
        console.log('[updateTableros] === VISTA INDIVIDUAL ===');
        if (athleteData) {
          console.log('[updateTableros] Hay atleta activo, construyendo datos...');
  
          const enhanced = {
            // mantenemos TODO tu objeto mejorado
            ...athleteData,
            currentAttempt: attemptNumber,
            activeAttempt: attemptNumber,
            isActive: true,
          };
  
          console.log('[updateTableros] Atleta mejorado:', {
            name: enhanced.name,
            currentResult: enhanced.currentResult,
            isActive: enhanced.isActive,
            currentAttempt: enhanced.currentAttempt
          });
  
          participantsForBoard = [enhanced];
          eventDataForBoard = {
            eventName:     heatDetails.eventName,
            eventSubtitle: heatDetails.eventSubtitle || '',
            eventNumber:   heatDetails.eventNumber,
            round:         heatDetails.round,
            heat:          heatDetails.heat,
            eventCode:     heatDetails.eventCode,
            participants:  participantsForBoard,
            activeAthlete: enhanced,
            displayMode:   'individual'
          };
  
          console.log('[updateTableros] EventData individual:', {
            activeAthleteName: eventDataForBoard.activeAthlete.name,
            participantsCount: eventDataForBoard.participants.length
          });
        } else {
          console.log('[updateTableros] Sin atleta activo - estado inicial');
          participantsForBoard = [];
          eventDataForBoard = {
            eventName:     heatDetails.eventName,
            eventSubtitle: heatDetails.eventSubtitle || '',
            eventNumber:   heatDetails.eventNumber,
            round:         heatDetails.round,
            heat:          heatDetails.heat,
            eventCode:     heatDetails.eventCode,
            participants:  [],
            activeAthlete: null,
            displayMode:   'initial'
          };
          console.log('[updateTableros] EventData inicial:', eventDataForBoard);
        }
      } else {
        console.log('[updateTableros] === VISTA MÚLTIPLE ===');
  
        participantsForBoard = allParticipantsWithPlaces.slice(0, rows).map(p => ({
          ...p,
          isActive:       athleteData?.idNumber === p.idNumber,
          currentAttempt: athleteData?.idNumber === p.idNumber ? attemptNumber : p.currentAttempt,
          activeAttempt:  athleteData?.idNumber === p.idNumber ? attemptNumber : p.activeAttempt,
          currentResult:  athleteData?.idNumber === p.idNumber ? athleteData.currentResult : p.currentResult,
          currentWind:    athleteData?.idNumber === p.idNumber ? athleteData.currentWind : p.currentWind
        }));
  
        eventDataForBoard = {
          eventName:     heatDetails.eventName,
          eventSubtitle: heatDetails.eventSubtitle || '',
          eventNumber:   heatDetails.eventNumber,
          round:         heatDetails.round,
          heat:          heatDetails.heat,
          eventCode:     heatDetails.eventCode,
          participants:  participantsForBoard,
          activeAthlete: athleteData
            ? { ...athleteData, activeAttempt: attemptNumber, isActive: true }
            : null,
          displayMode:   'multiple'
        };
      }
  
      console.log(`[updateTableros] === ENVIANDO A TABLERO ${cfg.name} ===`);
      console.log('[updateTableros] Payload final:', {
        configId: cfg.id,
        eventDataActiveAthlete: eventDataForBoard.activeAthlete?.name || 'null',
        eventDataParticipants:  eventDataForBoard.participants.length,
        displayRows:            rows
      });
  
      // 3) Llamada al refresco
      try {
        const ok = refreshTablero(cfg.id, {
          ...cfg,
          eventData: eventDataForBoard,
          settings:  { ...cfg.settings, displayRows: rows }
        });
        console.log(`[updateTableros] refreshTablero: ${ok ? 'éxito' : 'fallo'}`);
      } catch (err) {
        console.error(`[updateTableros] Error refrescando ${cfg.name}:`, err);
      }
    });
  
    console.log('[updateTableros] === FIN ===');
  }, [
    boardConfigs,
    heatKey,
    heatDetails,
    allParticipantsWithPlaces,
    refreshTablero
  ]);
  

  // Calcular lugares globales
  const globalPlaces = useMemo(() =>
    computeGlobalPlaces(
      participantsResults, 
      heatDetails.eventNumber, 
      heatDetails.round,
      heatDetails.eventCode  // Añadido parámetro eventCode
    ),
    [participantsResults, heatDetails.eventNumber, heatDetails.round, heatDetails.eventCode]
  );

  // ─────────────────────────────────────────────────────────────
  //  Calculamos dinámicamente cuántos carriles necesitamos
  // ─────────────────────────────────────────────────────────────
  const lanesCount = useMemo(() => {
    // Extraemos todos los lanes numéricos
    const numericLanes = participantsArray
      .map(p => parseInt(p.lane, 10))
      .filter(n => !isNaN(n));
    // El mayor asignado o al menos tantos como participantes
    const maxAssigned = numericLanes.length ? Math.max(...numericLanes) : 0;
    return Math.max(maxAssigned, participantsArray.length);
  }, [participantsArray]);

  // Cada fila corresponde a un carril, inicializado dinámico
  const [lanes, setLanes] = useState(() => Array(lanesCount).fill(null));

  // ---------------------------
  // 1) Inicializar Attempts
  // ---------------------------
  useEffect(() => {
    const eventSettings =
      testSettings[heatKey] || {
        numberOfAttempts: 6,
        numberOfPreliminaryAttempts: 3,
        hasFinalRound: true,
        athletesInFinal: 8,
      };

    const { numberOfAttempts } = eventSettings;
    const initializedAttempts = Array.from({ length: numberOfAttempts }, (_, i) => i + 1);
    setAttempts(initializedAttempts);
  }, [testSettings, heatKey]);

  // ---------------------------
  // 2) Asignar bestMark, place, etc. a cada participante
  // ---------------------------
  useEffect(() => {
    const eventSettings =
      testSettings[heatKey] || { 
        numberOfPreliminaryAttempts: 3, 
        hasFinalRound: true,
        athletesInFinal: 8 
      };
    const athletesInFinal = eventSettings.athletesInFinal || 8;

    // Estructura "estándar" para cada participante
    const updatedParticipants = participantsArray.map((p) => {
      const results =
        getParticipantResults(
          heatDetails.eventNumber,
          heatDetails.round,
          heatDetails.heat,
          p.idNumber // Aseguramos que siempre usamos idNumber
        ) || {};
      const bestMarkValue = calculateBestMark(p.idNumber, results, 'horizontal');

      

      return {
        ...p,
        attempts: results.attempts || [],
        special: results.special || '-',
        bestMark: bestMarkValue,
        notes: results.notes || '',
        points: results.points || 0,
      };
    });

    // Asignar places si existen rankings
    const rankingForHeat = rankings[heatKey] || [];
    
    const placeMapping = assignPlaces(
      updatedParticipants.map(p => ({
        id: p.idNumber, // Usamos idNumber como id para assignPlaces
        bestMark: p.bestMark,
        special: p.special || '-',
        attempts: p.attempts || []
      })),
      'horizontal',
      false,
      athletesInFinal
    );

    const finalParticipantsWithPlaces = updatedParticipants.map((participant) => {
      // Buscamos el lugar y passed usando idNumber consistentemente
      const ranking = placeMapping[participant.idNumber] || {};
      return {
        ...participant,
        place: ranking.place || getPlace(
          heatDetails.eventNumber,
          heatDetails.round,
          heatDetails.heat,
          participant.idNumber
        ) || '-',
        passed: ranking.passed || hasPassed(
          heatDetails.eventNumber,
          heatDetails.round,
          heatDetails.heat,
          participant.idNumber
        ) || false,
      };
    });

    setAllParticipantsWithPlaces(finalParticipantsWithPlaces);
  }, [
    heatKey,
    participantsArray,
    heatDetails,
    rankings,
    getParticipantResults,
    calculateBestMark,
    testSettings,
    getPlace,
    hasPassed
  ]);

  // ✅ NUEVO: Efecto para actualizar tableros cuando cambien los datos
  useEffect(() => {
    if (allParticipantsWithPlaces.length > 0) {
      // Si hay un atleta activo, mantenerlo, si no, limpiar
      updateTablerosWithActiveAthlete(activeAthlete, activeAttempt);
    }
  }, [allParticipantsWithPlaces, activeAthlete, activeAttempt, updateTablerosWithActiveAthlete]);

  // Cargar marcas de siembra desde Firestore
  useEffect(() => {
    const loadSeedMarks = async () => {
      const marks = {};
      for (const participant of participantsArray) {
        if (participant.idNumber) {
          try {
            const seedMarkData = await getSeedMarks(participant.idNumber);
            if (seedMarkData && seedMarkData[eventCode]?.seedMark) {
              marks[participant.idNumber] = seedMarkData[eventCode].seedMark;
            }
          } catch (error) {
            console.error(`Error cargando marca de siembra para ${participant.idNumber}:`, error);
          }
        }
      }
      setSeedMarks(marks);
    };
    
    if (participantsArray.length > 0) {
      loadSeedMarks();
    }
  }, [participantsArray, eventCode, getSeedMarks]);

  // ---------------------------
  // 3) Inicializar lanes
  // ---------------------------
  useEffect(() => {
    if (!allParticipantsWithPlaces.length) {
      setLanes(Array(lanesCount).fill(null));
      return;
    }

    const newLanes = Array(lanesCount).fill(null);
    let fillIndex = 0;

    allParticipantsWithPlaces.forEach((athlete) => {
      const laneNumber = parseInt(athlete.lane, 10);
      if (!isNaN(laneNumber) && laneNumber > 0 && laneNumber <= lanesCount) {
        newLanes[laneNumber - 1] = athlete;
      } else {
        while (fillIndex < lanesCount && newLanes[fillIndex] !== null) {
          fillIndex++;
        }
        if (fillIndex < lanesCount) {
          newLanes[fillIndex] = athlete;
          fillIndex++;
        }
      }
    });

    setLanes(newLanes);
  }, [allParticipantsWithPlaces, lanesCount]);
  // Añadir un nuevo carril
  const handleAddLane = () => {
    setLanes(prev => [...prev, null]);
    setSnackbar({ open: true, message: `Carril añadido.`, severity: 'success' });
  };

    // Handler "seguro" para añadir/reemplazar inline
    const handleInlineAdd = useCallback(async (laneNumber, newIdNumber) => {
      const trimmed = newIdNumber.trim();
      // 1) ¿ya existe en este heat?
      if (participantsArray.some(p => p.idNumber === trimmed)) {
        setSnackbar({
          open: true,
          message: `El atleta ${trimmed} ya está en este heat.`,
          severity: 'error'
        });
        return;
      }
      // 2) ¿existe en tu base de atletas?
      const found = pplData.find(p => p.idNumber === trimmed);
      if (!found) {
        setSnackbar({
          open: true,
          message: `Atleta ${trimmed} no encontrado.`,
          severity: 'error'
        });
        return;
      }
      // 3) Añadimos normalmente
      updateHeatParticipants(
        heatDetails.eventNumber,
        heatDetails.round,
        heatDetails.heat,
        found.idNumber,
        laneNumber,
        'add',
        { ...found, lane: laneNumber }
      );
      setSnackbar({
        open: true,
        message: `${found.fullName} añadido al carril ${laneNumber}.`,
        severity: 'success'
      });
      calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
    }, [participantsArray, pplData, heatDetails, updateHeatParticipants, calculateRankings]);

  // ---------------------------
  // 1) Inicializar Attempts
  // ---------------------------
  useEffect(() => {
    const eventSettings =
      testSettings[heatKey] || {
        numberOfAttempts: 6,
        numberOfPreliminaryAttempts: 3,
        hasFinalRound: true,
        athletesInFinal: 8,
      };

    const { numberOfAttempts } = eventSettings;
    const initializedAttempts = Array.from({ length: numberOfAttempts }, (_, i) => i + 1);
    setAttempts(initializedAttempts);
  }, [testSettings, heatKey]);

   // Añadir un atleta en un carril vacío (vía prompt)
   const handleAddAthlete = useCallback((laneNumber) => {
    const idNum = window.prompt('Ingrese el número de atleta (idNumber):');
    if (!idNum) return;
    const found = pplData.find(p => p.idNumber === idNum.trim());
    if (!found) {
      setSnackbar({ open: true, message: `Atleta con idNumber ${idNum} no encontrado.`, severity: 'error' });
      return;
    }
    if (participantsArray.some(p => p.idNumber === found.idNumber)) {
      setSnackbar({ open: true, message: `${found.fullName} ya está en este heat.`, severity: 'warning' });
      return;
    }
    updateHeatParticipants(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      found.idNumber,
      laneNumber,
      'add',
      { ...found, lane: laneNumber }
    );
    setSnackbar({ open: true, message: `${found.fullName} añadido al carril ${laneNumber}.`, severity: 'success' });
    calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
  }, [
    pplData,
    participantsArray,
    heatDetails,
    updateHeatParticipants,
    calculateRankings
  ]);

    // Gestión de marca de siembra - Ahora usa saveSeedMarks como en TrackEvents
    const handleSaveSeed = async (idNumber, seedMark) => {
      try {
        await saveSeedMarks(idNumber, eventCode, seedMark);
        
        // Actualizar el estado local de marcas de siembra
        setSeedMarks(prevMarks => ({
          ...prevMarks,
          [idNumber]: seedMark
        }));
        
        // Actualizar la UI localmente - actualiza el array de lanes
        setLanes(currentLanes => {
          return currentLanes.map(lane => {
            if (lane && lane.idNumber === idNumber) {
              return {
                ...lane,
                seedMark: seedMark
              };
            }
            return lane;
          });
        });
    
        // También actualiza el participante seleccionado para mantener consistencia con el modal
        if (selectedSeedParticipant && selectedSeedParticipant.idNumber === idNumber) {
          setSelectedSeedParticipant({
            ...selectedSeedParticipant,
            seedMark: seedMark
          });
        }
        
        setOpenSeedMarkModal(false);
        setSnackbar({ 
          open: true, 
          message: `Marca de siembra guardada: ${seedMark}`, 
          severity: 'success' 
        });
      } catch (error) {
        console.error('Error al guardar marca de siembra:', error);
        setSnackbar({ 
          open: true, 
          message: `Error al guardar marca: ${error.message}`, 
          severity: 'error' 
        });
      }
    };
  
    const handleDeleteSeedMark = (participantId) => {
      try {
        // Guardar marca de siembra con valor vacío (esto efectivamente la "elimina")
        saveSeedMarks(participantId, eventCode, "");
        
        // Actualizar el estado local
        setSeedMarks(prevMarks => {
          const newMarks = { ...prevMarks };
          delete newMarks[participantId]; // Eliminar la entrada del mapa
          return newMarks;
        });
        
        // Actualizar la UI
        setLanes(currentLanes => {
          return currentLanes.map(lane => {
            if (lane && lane.idNumber === participantId) {
              return {
                ...lane,
                seedMark: ""
              };
            }
            return lane;
          });
        });
        
        // Cerrar el modal
        setOpenSeedMarkModal(false);
        setSelectedSeedParticipant(null);
        
        setSnackbar({ 
          open: true, 
          message: "Marca de siembra eliminada", 
          severity: 'success' 
        });
      } catch (error) {
        console.error('Error al eliminar marca de siembra:', error);
        setSnackbar({ 
          open: true, 
          message: `Error al eliminar marca: ${error.message}`, 
          severity: 'error' 
        });
      }
    };
  
    // Handle notes update
    const handleNotesUpdate = (participantId, notes) => {
      updateParticipantResults(
        heatDetails.eventNumber,
        heatDetails.round,
        heatDetails.heat,
        participantId,
        { notes }
      );
      setSnackbar({ open: true, message: 'Notas guardadas.', severity: 'success' });
    };

  // ✅ NUEVO: Efecto para actualizar tableros cuando cambien los datos
  useEffect(() => {
    if (allParticipantsWithPlaces.length > 0) {
      // Si hay un atleta activo, mantenerlo, si no, limpiar
      updateTablerosWithActiveAthlete(activeAthlete, activeAttempt);
    }
  }, [allParticipantsWithPlaces, activeAthlete, activeAttempt, updateTablerosWithActiveAthlete]);

  // Cargar marcas de siembra desde Firestore
  useEffect(() => {
    const loadSeedMarks = async () => {
      const marks = {};
      for (const participant of participantsArray) {
        if (participant.idNumber) {
          try {
            const seedMarkData = await getSeedMarks(participant.idNumber);
            if (seedMarkData && seedMarkData[eventCode]?.seedMark) {
              marks[participant.idNumber] = seedMarkData[eventCode].seedMark;
            }
          } catch (error) {
            console.error(`Error cargando marca de siembra para ${participant.idNumber}:`, error);
          }
        }
      }
      setSeedMarks(marks);
    };
    
    if (participantsArray.length > 0) {
      loadSeedMarks();
    }
  }, [participantsArray, eventCode, getSeedMarks]);

  // ---------------------------
  // 3) Inicializar lanes
  // ---------------------------
  useEffect(() => {
    if (!allParticipantsWithPlaces.length) {
      setLanes(Array(lanesCount).fill(null));
      return;
    }

    const newLanes = Array(lanesCount).fill(null);
    let fillIndex = 0;

    allParticipantsWithPlaces.forEach((athlete) => {
      const laneNumber = parseInt(athlete.lane, 10);
      if (!isNaN(laneNumber) && laneNumber > 0 && laneNumber <= lanesCount) {
        newLanes[laneNumber - 1] = athlete;
      } else {
        while (fillIndex < lanesCount && newLanes[fillIndex] !== null) {
          fillIndex++;
        }
        if (fillIndex < lanesCount) {
          newLanes[fillIndex] = athlete;
          fillIndex++;
        }
      }
    });

    setLanes(newLanes);
  }, [allParticipantsWithPlaces, lanesCount]);
  // -----------------------------------
  // 4) Manejo de drag & drop
  // -----------------------------------
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Buscamos por idNumber para mantener consistencia
    const oldIndex = lanes.findIndex((lane, idx) =>
      lane ? String(lane.idNumber) === active.id : `empty-${idx + 1}` === active.id
    );
    const newIndex = lanes.findIndex((lane, idx) =>
      lane ? String(lane.idNumber) === over.id : `empty-${idx + 1}` === over.id
    );

    if (oldIndex < 0 || newIndex < 0) return;
    
    const newLanes = [...lanes];
    const moved = newLanes[oldIndex];
    newLanes[oldIndex] = newLanes[newIndex];
    newLanes[newIndex] = moved;

    newLanes.forEach((ath, idx) => {
      if (ath) {
        updateHeatParticipants(
          heatDetails.eventNumber,
          heatDetails.round,
          heatDetails.heat,
          ath.idNumber, // Siempre usar idNumber para consistencia
          idx + 1,
          'update'
        );
      }
    });

    setLanes(newLanes);
    setSnackbar({
      open: true,
      message: 'Se intercambiaron los atletas correctamente.',
      severity: 'success',
    });
    
    calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
  };

  // Items para dnd-kit - Usar idNumber para los IDs
  const items = lanes.map((lane, idx) => 
    lane ? String(lane.idNumber) : `empty-${idx + 1}`
  );

  // -----------------------------------
  // 5) Funciones para ResultModal
  // -----------------------------------

// ✅ FUNCIÓN CORREGIDA para abrir modal de resultados - SIN MENSAJES
const handleOpenResultModal = (athlete, attemptNumber) => {
  if (!athlete) {
    console.error('[HorizontalEvents] Atleta no válido:', athlete);
    return;
  }

  console.log('[HorizontalEvents] === ABRIENDO MODAL ===');
  console.log('[HorizontalEvents] Atleta recibido:', athlete);
  console.log('[HorizontalEvents] Intento:', attemptNumber);

  // ✅ CREAR OBJETO ATLETA ACTIVO CON DATOS COMPLETOS Y CORREGIDOS
  const activeAthleteData = {
    // IDs y datos básicos
    idNumber: athlete.idNumber,
    
    // ✅ NOMBRES CORREGIDOS: Priorizar fullName, luego construir desde firstName/lastName
    name: athlete.fullName || 
          `${athlete.firstName || ''} ${athlete.lastName || ''}`.trim() || 
          athlete.name || 
          'Sin nombre',
    fullName: athlete.fullName || 
              `${athlete.firstName || ''} ${athlete.lastName || ''}`.trim() || 
              athlete.name || 
              'Sin nombre',
    firstName: athlete.firstName || '',
    lastName: athlete.lastName || '',
    
    // Equipo y afiliación
    team: athlete.affiliation || athlete.team || 'Sin equipo',
    affiliation: athlete.affiliation || athlete.team || 'Sin equipo',
    
    // Datos de competencia
    lane: athlete.lane || '',
    position: athlete.place || athlete.position || '-',
    place: athlete.place || athlete.position || '-',
    
    // Resultados y estadísticas
    attempts: athlete.attempts || [],
    bestMark: athlete.bestMark || '-',
    special: athlete.special || '-',
    wind: athlete.wind || null,
    notes: athlete.notes || '',
    points: athlete.points || 0,
    
    // ✅ DATOS CRÍTICOS PARA EL ESTADO ACTIVO
    currentAttempt: attemptNumber,
    activeAttempt: attemptNumber,
    currentResult: null, // ✅ Inicialmente sin resultado
    currentWind: null,   // ✅ Inicialmente sin viento
    isActive: true       // ✅ MARCAR COMO ACTIVO
  };

  console.log('[HorizontalEvents] Atleta activo creado:', {
    name: activeAthleteData.name,
    fullName: activeAthleteData.fullName,
    intento: activeAthleteData.currentAttempt,
    isActive: activeAthleteData.isActive
  });
  
  // ✅ ESTABLECER ATLETA ACTIVO INMEDIATAMENTE
  setActiveAthlete(activeAthleteData);
  setActiveAttempt(attemptNumber);
  
  // ✅ ACTUALIZAR TABLEROS CON EL ATLETA ACTIVO (SIN RESULTADO)
  updateTablerosWithActiveAthlete(activeAthleteData, attemptNumber);
  
  // Preparar datos del modal
  const participantResult = getParticipantResults(
    heatDetails.eventNumber,
    heatDetails.round,
    heatDetails.heat,
    athlete.idNumber
  ) || {};
  
  const attemptData = participantResult.attempts?.[attemptNumber - 1] || {};
  
  // Establecer estados del modal
  setInitialValues({
    result: attemptData.result || '',
    wind: attemptData.wind || '',
  });
  setSelectedAttempt(attemptNumber);
  setSelectedParticipant(activeAthleteData);
  setOpenResultModal(true);
  
  console.log('[HorizontalEvents] Modal abierto, tableros actualizados');
};

// ✅ FUNCIÓN LIMPIA para cerrar modal
const handleCloseResultModal = () => {
  console.log('[HorizontalEvents] === CERRANDO MODAL ===');
  
  setOpenResultModal(false);
  setSelectedParticipant(null);
  setSelectedAttempt(null);
  setInitialValues({});
  
  // ✅ LIMPIAR ATLETA ACTIVO AL CERRAR MODAL
  console.log('[HorizontalEvents] Limpiando atleta activo');
  setActiveAthlete(null);
  setActiveAttempt(null);
  
  // ✅ ACTUALIZAR TABLEROS SIN ATLETA ACTIVO (ESTADO INICIAL)
  clear(heatDetails)
  
  console.log('[HorizontalEvents] Modal cerrado, tableros en estado inicial');
};

// ✅ FUNCIÓN LIMPIA para guardar resultados
const handleSaveResult = (participantId, resultData) => {
  const attemptNumber = selectedAttempt;
  if (!attemptNumber) return;

  console.log('[HorizontalEvents] === GUARDANDO RESULTADO ===');
  console.log('[HorizontalEvents] Datos:', { participantId, attemptNumber, resultData });

  // Obtener resultados actuales
  const currentResults = getParticipantResults(
    heatDetails.eventNumber,
    heatDetails.round,
    heatDetails.heat,
    participantId
  ) || {};

  // Actualizar intentos
  const updatedAttempts = [...(currentResults.attempts || [])];
  updatedAttempts[attemptNumber - 1] = resultData;

  const newBestMark = calculateBestMark(
    participantId,
    { ...currentResults, attempts: updatedAttempts },
    'horizontal'
  );
  
  // ✅ ACTUALIZAR ATLETA ACTIVO CON RESULTADO
  if (activeAthlete && activeAthlete.idNumber === participantId) {
    const updatedActiveAthlete = {
      ...activeAthlete,
      attempts: updatedAttempts,
      bestMark: newBestMark,
      currentResult: resultData.result, // ✅ IMPORTANTE: Resultado actual
      currentWind: resultData.wind,     // ✅ IMPORTANTE: Viento actual
      activeAttempt: activeAttempt,
      currentAttempt: activeAttempt,
      isActive: true // ✅ Mantener activo
    };
    
    console.log('[HorizontalEvents] Actualizando atleta activo con resultado:', {
      name: updatedActiveAthlete.name,
      currentResult: updatedActiveAthlete.currentResult,
      currentWind: updatedActiveAthlete.currentWind,
      hasResult: !!updatedActiveAthlete.currentResult
    });
    
    setActiveAthlete(updatedActiveAthlete);
    
    // ✅ ACTUALIZAR TABLEROS CON RESULTADO
    saveResult(heatDetails, updatedActiveAthlete, activeAttempt)
  }

  // ✅ ACTUALIZACIÓN LOCAL INMEDIATA
  const updatedAthleteData = {
    attempts: updatedAttempts,
    bestMark: newBestMark,
    currentResult: resultData.result,
    currentWind: resultData.wind
  };

  updateLocalAthleteData(participantId, updatedAthleteData);
  
  setAllParticipantsWithPlaces(currentPlaces => {
    return currentPlaces.map(place => {
      if (place.idNumber === participantId) {
        const bestMarkValue = calculateBestMark(
          participantId, 
          {...currentResults, attempts: updatedAttempts}, 
          'horizontal'
        );
        
        return {
          ...place,
          attempts: updatedAttempts,
          bestMark: bestMarkValue,
          currentResult: resultData.result,
          currentWind: resultData.wind
        };
      }
      return place;
    });
  });

  // Actualizar en Firestore (segundo plano)
  updateParticipantResults(
    heatDetails.eventNumber,
    heatDetails.round,
    heatDetails.heat,
    participantId,
    {
      ...currentResults,
      attempts: updatedAttempts,
    }
  );

  console.log('[HorizontalEvents] Resultado guardado exitosamente');
  setOpenResultModal(false);
  // Calcular rankings después
  setTimeout(() => {
    calculateRankings(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat
    );
  }, 100);
};


  const updateLocalAthleteData = (participantId, updatedData) => {
    setLanes(currentLanes => {
      return currentLanes.map(lane => {
        if (lane && lane.idNumber === participantId) {
          return { ...lane, ...updatedData };
        }
        return lane;
      });
    });
    
    setAllParticipantsWithPlaces(currentList => {
      return currentList.map(participant => {
        if (participant.idNumber === participantId) {
          return { ...participant, ...updatedData };
        }
        return participant;
      });
    });
  };

  const handleDeleteResult = (participantId) => {
    const attemptNumber = selectedAttempt;
    if (!attemptNumber) return;

    const currentResults =
      getParticipantResults(
        heatDetails.eventNumber,
        heatDetails.round,
        heatDetails.heat,
        participantId
      ) || {};

    const updatedAttempts = [...(currentResults.attempts || [])];
    if (updatedAttempts[attemptNumber - 1]) {
      updatedAttempts[attemptNumber - 1] = {};
    }

    updateParticipantResults(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      participantId,
      {
        ...currentResults,
        attempts: updatedAttempts,
      }
    );

    setSnackbar({
      open: true,
      message: 'Intento eliminado.',
      severity: 'info',
    });

    calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
    handleCloseResultModal();
  };

  const handleSpecialAction = (participantId, specialStatus) => {
    updateParticipantResults(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      participantId,
      { special: specialStatus }
    );
    setSnackbar({
      open: true,
      message: `Opción especial: ${specialStatus}`,
      severity: 'info',
    });
    calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
  };

  // Revisa si todos los atletas han completado sus intentos preliminares
  const allPreliminaryAttemptsCompleted = () => {
    const prelim = testSettings[heatKey]?.numberOfPreliminaryAttempts || 3;
    if (prelim === 0) return true;

    // Revisar cada lane, si hay atleta, que tenga sus preliminares
    return lanes.every((athlete) => {
      if (!athlete) return true; // carril vacío no afecta
      const results =
        getParticipantResults(
          heatDetails.eventNumber, 
          heatDetails.round, 
          heatDetails.heat, 
          athlete.idNumber
        ) || {};
        
      if (results.special && results.special !== '-') return true;
      
      const validAttempts = (results.attempts || [])
        .slice(0, prelim)
        .filter((att) => att.result && att.result.trim() !== '');
        
      return validAttempts.length === prelim;
    });
  };

  // ✅ NUEVO: Función para resaltar botón del atleta activo
  const isActiveAttempt = (athlete, attemptNumber) => {
    return activeAthlete && 
           activeAthlete.idNumber === athlete.idNumber && 
           activeAttempt === attemptNumber;
  };

// ✅ FUNCIÓN MEJORADA: Limpiar atleta activo y regresar a estado inicial
const handleClearActiveAthlete = useCallback(() => {
  console.log('[HorizontalEvents] Limpiando atleta activo - regresando a estado inicial');
  
  setActiveAthlete(null);
  setActiveAttempt(null);
  clear(heatDetails);  
  // ✅ ACTUALIZAR TABLEROS AL ESTADO INICIAL (logos y nombre del evento)
  updateTablerosWithActiveAthlete(null, null);
  
  setSnackbar({
    open: true,
    message: 'Tablero regresado a estado inicial',
    severity: 'info',
  });
}, [updateTablerosWithActiveAthlete]);

// ✅ FUNCIÓN ADICIONAL: Cambiar a vista de espera (atleta seleccionado sin resultado)
const handleSetWaitingState = useCallback((athlete) => {
  console.log('[HorizontalEvents] Estableciendo estado de espera para:', athlete.name);
  
  const waitingAthleteData = {
    ...athlete,
    currentResult: null, // ✅ Sin resultado actual
    currentWind: null,
    activeAttempt: 1, // Comenzar en intento 1
    isActive: true
  };
  
  setActiveAthlete(waitingAthleteData);
  setActiveAttempt(1);
  
  // ✅ ACTUALIZAR TABLEROS AL ESTADO DE ESPERA
  updateTablerosWithActiveAthlete(waitingAthleteData, 1);
  
  setSnackbar({
    open: true,
    message: `${athlete.name} listo para competir`,
    severity: 'warning',
  });
}, [updateTablerosWithActiveAthlete]);

  // Renderizar cada botón de intento
  const renderAttemptCells = athlete => {
    if (!athlete) {
      return attempts.map((_, i) => (
        <TableCell key={i} align="center" sx={responsiveStyles.tableCell}>-</TableCell>
      ));
    }
    
    const results = getParticipantResults(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      athlete.idNumber
    ) || {};
    
    return attempts.map(attemptNumber => {
      const attemptData = results.attempts?.[attemptNumber - 1] || {};
      const hasSpecial = ['DNS','DNF','DQ','SCR','NM'].includes(athlete.special);
      const isActive = isActiveAttempt(athlete, attemptNumber);
      
      // Solo mostrar el viento en los eventos de salto y si está habilitado
      const showWind = isJumpEvent && testSettings[heatKey]?.enableWind && attemptData.wind;
      
      return (
        <TableCell key={attemptNumber} align="center" sx={responsiveStyles.tableCell}>
          <Button
            variant={isActive ? "contained" : "outlined"}
            color={isActive ? "primary" : "inherit"}
            onClick={() => handleOpenResultModal(athlete, attemptNumber)}
            disabled={hasSpecial}
            sx={{
              ...responsiveStyles.button,
              ...(isActive ? {
                fontWeight: 'bold',
                backgroundColor: 'primary.main',
                color: 'white',
                boxShadow: '0 0 10px rgba(25, 118, 210, 0.5)',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { boxShadow: '0 0 10px rgba(25, 118, 210, 0.5)' },
                  '50%': { boxShadow: '0 0 20px rgba(25, 118, 210, 0.8)' },
                  '100%': { boxShadow: '0 0 10px rgba(25, 118, 210, 0.5)' }
                }
              } : attemptData.result ? {
                fontWeight: 'bold',
                borderColor: 'primary.main',
                '&:hover': {
                  borderColor: 'success.dark',
                  backgroundColor: 'primary.light',
                  opacity: 0.9,
                }
              } : {})
            }}
          >
            {attemptData.result || '-'}
            {showWind ? ` (${attemptData.wind})` : ''}
          </Button>
        </TableCell>
      );
    });
  };

  // Render Mejor Marca y Lugar
  const renderBestMarkAndPlace = (athlete) => {
    if (!athlete) {
      return (
        <>
          <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>
          <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>
        </>
      );
    }
    
    const hasSpecial =
      athlete.special &&
      athlete.special !== '-' &&
      specialStatuses.includes(athlete.special);
      
    const bestMark = hasSpecial
      ? athlete.special.toUpperCase()
      : typeof athlete.bestMark === 'number'
      ? `${athlete.bestMark.toFixed(2)} m`
      : athlete.bestMark === 'NM'
      ? 'NM'
      : '-';

    // Obtener el lugar desde getPlace para asegurar consistencia
    const place = getPlace(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      athlete.idNumber
    ) || athlete.place || '-';

    return (
      <>
        <TableCell align="center" sx={responsiveStyles.tableCell}>{bestMark}</TableCell>
        <TableCell align="center" sx={responsiveStyles.tableCell}>{place}</TableCell>
      </>
    );
  };

  // Render columna de marca de siembra
  const renderSeedMarkCell = (athlete) => {
    if (!athlete) {
      return <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>;
    }

    return (
      <TableCell align="center" sx={responsiveStyles.tableCell}>
        <Button 
          variant="outlined" 
          size="small"
          onClick={() => {
            setSelectedSeedParticipant(athlete);
            setOpenSeedMarkModal(true);
          }}
          sx={{
            ...responsiveStyles.button,
            color: 'success.main',
            borderColor: 'success.main',
            '&:hover': {
              borderColor: 'success.dark',
              backgroundColor: 'success.light',
              opacity: 0.9,
            },
            ...(seedMarks[athlete.idNumber] ? {
              fontWeight: 'bold',
              backgroundColor: 'rgba(76, 175, 80, 0.1)'
            } : {})
          }}
        >
          {seedMarks[athlete.idNumber] || athlete.seedMark || '—'}
        </Button>
      </TableCell>
    );
  };

  // Render columna de lugar general
  const renderGeneralPlaceCell = (athlete) => {
    if (!athlete) {
      return <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>;
    }
  
    const globalPlace = globalPlaces[athlete.idNumber] ?? '-';
    return (
      <TableCell 
        align="center" 
        sx={{ 
          ...responsiveStyles.tableCell,
          fontWeight: globalPlace !== '-' ? 'bold' : 'normal',
          color: globalPlace !== '-' ? 'text.primary' : 'text.secondary' 
        }}
      >
        {globalPlace}
      </TableCell>
    );
  };

  // Render columna de notas
  const renderNotesCell = (athlete) => {
    if (!athlete) {
      return <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>;
    }
  
    return (
      <TableCell align="center" sx={responsiveStyles.tableCell}>
        <TextField
          size="small"
          value={athlete.notes || ''}
          onChange={(e) => handleNotesUpdate(athlete.idNumber, e.target.value)}
          variant="outlined"
          placeholder="Notas"
          sx={{
            ...responsiveStyles.inputField,
            '& .MuiInputBase-input': {
              // Aplicar tamaño de fuente responsivo
              fontSize: responsiveStyles.tableCell.fontSize,
              padding: responsiveStyles.tableCell.padding
            }
          }}
        />
      </TableCell>
    );
  };

  // Render columna de puntaje
  const renderPointsCell = (athlete) => {
    if (!athlete || !enablePoints) {
      return null;
    }

    return (
      <TableCell align="center" sx={responsiveStyles.tableCell}>
        {athlete.points || 0}
      </TableCell>
    );
  };

  // Render columna de acciones
  const renderActionsCell = athlete => (
    <TableCell align="center" sx={responsiveStyles.tableCell}>
      { athlete ? (
        <ActionColumn
          participant={athlete}
          handleEditLane={(upd, newLane) => {
            updateHeatParticipants(
              heatDetails.eventNumber,
              heatDetails.round,
              heatDetails.heat,
              upd.idNumber, 
              Number(newLane), 
              'update'
            );
            setSnackbar({ open:true, message:'Carril actualizado.', severity:'success' });
          }}
          handleRemoveAthlete={rem => {
            updateHeatParticipants(
              heatDetails.eventNumber,
              heatDetails.round,
              heatDetails.heat,
              rem.idNumber, 
              null, 
              'remove'
            );
            setSnackbar({ open:true, message:'Atleta eliminado.', severity:'info' });
          }}
          handleActionSelect={spec => {
            updateParticipantResults(
              heatDetails.eventNumber,
              heatDetails.round,
              heatDetails.heat,
              athlete.idNumber, 
              { special: spec }
            );
            calculateRankings(heatDetails.eventNumber, heatDetails.round, heatDetails.heat);
            setSnackbar({ open:true, message:`Especial: ${spec}`, severity:'info' });
          }}
          isTrackEvent={false}
          responsiveStyles={responsiveStyles} 
        />
      ) : '—' }
    </TableCell>
  );
  // Render principal
  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      {/* ✅ NUEVO: Indicador de atleta activo */}
      {activeAthlete && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: 'primary.light', 
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" color="primary.dark">
            🎯 Atleta Activo: {activeAthlete.name} - Intento {activeAttempt}
          </Typography>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={handleClearActiveAthlete}
            sx={{ color: 'primary.dark', borderColor: 'primary.dark' }}
          >
            Limpiar
          </Button>
        </Box>
      )}

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <TableContainer 
            component={Paper}
            sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[300],
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.grey[100],
              },
            }}
          >
            <Table stickyHeader size={isMobile ? "small" : isTablet ? "medium" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>Mover</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>Carril</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>
                    {isMobile ? "Num" : "Número"}
                  </TableCell>
                  <TableCell align="left" sx={responsiveStyles.tableCell}>
                    {isMobile ? "Nombre" : "Nombre y Equipo"}
                  </TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>Siembra</TableCell>

                  {/* Intentos */}
                  {attempts.map((att) => (
                    <TableCell
                      key={`header-att-${att}`}
                      align="center"
                      sx={responsiveStyles.tableCell}
                    >
                      {isMobile ? `Int ${att}` : `Intento ${att}`}
                    </TableCell>
                  ))}

                  {/* Mejor marca y lugar */}
                  <TableCell align="center" sx={responsiveStyles.tableCell}>
                    {isMobile ? "Mejor" : "Mejor Marca"}
                  </TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>Lugar</TableCell>
                  
                  {/* Lugar General */}
                  <TableCell align="center" sx={responsiveStyles.tableCell}>
                    {isMobile ? "L.Gen" : "Lugar Gen"}
                  </TableCell>
                  
                  {/* Puntaje (opcional) */}
                  {enablePoints && (
                    <TableCell align="center" sx={responsiveStyles.tableCell}>Puntos</TableCell>
                  )}
                  
                  {/* Notas */}
                  <TableCell align="center" sx={responsiveStyles.tableCell}>Notas</TableCell>
                  
                  <TableCell align="center" sx={responsiveStyles.tableCell}>
                    <ActionColumnHeader
                      handleAddAthlete={handleAddLane}
                      responsiveStyles={responsiveStyles}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lanes.map((athlete, index) => {
                  const laneNumber = index + 1;
                  const isPassed = athlete && hasPassed(
                    heatDetails.eventNumber,
                    heatDetails.round,
                    heatDetails.heat,
                    athlete.idNumber
                  );
                  const isActiveAthleteRow = activeAthlete && athlete && athlete.idNumber === activeAthlete.idNumber;
                  
                  const rowStyle = isActiveAthleteRow
                    ? { backgroundColor: 'rgba(25, 118, 210, 0.1)', border: '2px solid', borderColor: 'primary.main' }
                    : isPassed
                      ? { backgroundColor: '#c8e6c9' }
                      : index % 2 === 0
                        ? { backgroundColor: theme.palette.action.hover }
                        : {};

                  return (
                    <LaneSortableItem
                      key={athlete ? `ath-${athlete.idNumber}` : `empty-${laneNumber}`}
                      laneNumber={laneNumber}
                      athlete={athlete}
                      onAddAthlete={handleAddAthlete}
                      onReplaceAthlete={handleInlineAdd}
                      additionalCells={
                        <>
                          {/* Marca de Siembra */}
                          {renderSeedMarkCell(athlete)}
                          
                          {/* Intentos */}
                          {renderAttemptCells(athlete)}
                          
                          {/* Mejor marca y lugar */}
                          {renderBestMarkAndPlace(athlete)}
                          
                          {/* Lugar General */}
                          {renderGeneralPlaceCell(athlete)}
                          
                          {/* Puntaje (opcional) */}
                          {enablePoints && renderPointsCell(athlete)}
                          
                          {/* Notas */}
                          {renderNotesCell(athlete)}
                          
                          {/* Acciones */}
                          {renderActionsCell(athlete)}
                        </>
                      }
                      rowStyle={rowStyle}
                      cellStyle={responsiveStyles.tableCell}
                      buttonStyle={responsiveStyles.button}
                      typographyStyle={responsiveStyles.typography}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SortableContext>
      </DndContext>

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <DownloadHorizontalLffButton
          heatDetails={heatDetails}
          rankings={rankings}
          getParticipantResults={getParticipantResults}
        />
      </Box>
      {/* Modal de Resultados */}
      {selectedParticipant && (
        <ResultModal
          open={openResultModal}
          onClose={handleCloseResultModal}
          participant={selectedParticipant}
          eventType={heatDetails.eventCode}
          onSelectAthlete={(ath, att) => selectAthlete(heatDetails, ath, att)}
          onSaveResult={(ath, att) => saveResult(heatDetails, ath, att)}
          onClear={() => clear(heatDetails)}
          isTrackEvent={false}
          onSave={handleSaveResult}
          onDelete={(participantId) =>
            handleDeleteResult(participantId)
          }
          windEnabled={
            // Solo habilitar viento para eventos de salto, no para lanzamientos
            isJumpEvent && testSettings[heatKey]?.enableWind || false
          }
          initialValues={{
            result: initialValues.result || '',
            wind: initialValues.wind || '',
            notes: selectedParticipant.notes || '',
          }}
          attemptNumber={selectedAttempt}
        />
      )}

      {/* Modal de Marca de Siembra */}
      {selectedSeedParticipant && (
        <ResultModal
          open={openSeedMarkModal}
          onClose={() => {
            setSelectedSeedParticipant(null);
            setOpenSeedMarkModal(false);
          }}
          participant={selectedSeedParticipant}
          eventType={heatDetails.eventCode}
          isTrackEvent={false}
          seedMarkMode={true} // activamos el modo especial
          onSaveSeedMark={handleSaveSeed}
          onDelete={handleDeleteSeedMark} 
          initialValues={{
            seedMark: selectedSeedParticipant ? 
            (seedMarks[selectedSeedParticipant.idNumber] || '') : '',
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbar({ ...snackbar, open: false })
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={() =>
            setSnackbar({ ...snackbar, open: false })
          }
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HorizontalEvents;